import { useState } from "react";
import { Link, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { authAPI } from "../../../services/apiService";
import { AuthLayout } from "../../components/auth/AuthLayout";
import { Showcase } from "../../components/auth/Showcase";
import { Stepper } from "../../components/auth/Stepper";
import { FormError, PasswordInput } from "../../components/auth/fields";
import {
  AUTH_PATHS,
  COUNTRY_OPTIONS,
  DIAL_OPTIONS,
  SignupProvider,
  dialCodeOf,
  isIndia,
  gstStateCodes,
  indianStates,
  orgTypes,
  stepPath,
  useSignup,
} from "../../components/auth/signupState";
import { Checkbox } from "../../components/crm/contactsKit";
import { Field, GhostButton, PrefixInput, SelectInput, TextInput, isEmail } from "../../components/crm/leadsKit";
import { PrimaryButton } from "../../components/ui";
import { SearchSelect } from "../../components/SearchSelect";
import { TERMS_URL, PRIVACY_URL } from "../../components/auth/AuthLayout";
import Verify from "./Verify";
import Setup from "./Setup";

/**
 * Signup (design: screens/Signup.tsx + Verify + Setup), on the same API calls
 * and payloads as the old pages/Register.js:
 *   Your details  → POST /api/auth/check-email { email }
 *   Tax details   → POST /api/auth/send-otp { name, email, password, phone, countryCode, channel: "whatsapp" }
 *                   (item 267: the code goes on WhatsApp, or by email when WhatsApp can't
 *                   deliver it; the response's `channel` says which)
 *   Verify        → POST /api/auth/verify-otp { email, otp, organization…, organizationType,
 *                   website, legalName } (creates the account and organisation, returns
 *                   token + user), resend → POST /api/auth/resend-otp { email, channel }
 *   Account setup → no API (the account is already created by verify-otp)
 *
 * Mount at `${AUTH_PATHS.signup}/*`. It does its own "already signed in" check on
 * arrival (like PublicRoute), so it must NOT sit inside PublicRoute: a token
 * appears halfway through (after Verify) and PublicRoute would bounce the
 * Account setup step to the dashboard.
 *
 * Type, Website and Legal business name are saved on the organisation (item 266).
 *
 * International sign-up (old Register.js country picker and Tax ID / VAT): the country is asked first;
 * it sets the mobile number's dialling code (changeable), and outside India the address takes any
 * state / region and postal code and the tax step asks for a tax ID instead of GSTIN and PAN. The
 * backend gives an organisation whose dialling code isn't +91 a 7-day trial of the top paid plan
 * (authController.verifyOTP → subscriptionService.isOutsideIndia), and the tax step says so.
 *
 * Retrying: a sign-up that sent a code but was never verified doesn't block the
 * email (the backend skips such rows in check-email and send-otp), so going back
 * and changing details sends a fresh code with the new details.
 */

/** What a code was sent for: changing any of these means a new send-otp. */
const otpKeyOf = (data) => [data.email.trim(), data.name, data.dialCode, data.phone.replace(/\D/g, ""), data.password].join("|");

function SignupShell({ step, title, sub, aside, onSubmit, back, submitLabel = "Continue", loading, error, children }) {
  const navigate = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    onSubmit();
  };
  return (
    <AuthLayout
      topRight={
        <p className="text-small text-muted">
          Have an account?{" "}
          <Link to={AUTH_PATHS.signin} className="font-semibold text-ink underline-offset-4 hover:underline">
            Sign in
          </Link>
        </p>
      }
      aside={aside}
    >
      <form key={step} onSubmit={submit} noValidate className="flex animate-fade-in flex-col gap-8">
        <Stepper current={step} />
        <div className="flex flex-col gap-2">
          <h1 className="text-h1 font-semibold">{title}</h1>
          <p className="text-body text-muted">{sub}</p>
        </div>
        {/* Not in the design: the server's error (e.g. the code email failed) has to show somewhere. */}
        {error && <FormError>{error}</FormError>}
        <div className="flex flex-col gap-5">{children}</div>
        <div className="flex items-center gap-3">
          {back && (
            <GhostButton type="button" onClick={() => navigate(back)}>
              <ArrowLeft size={18} strokeWidth={1.75} />
              Back
            </GhostButton>
          )}
          <PrimaryButton type="submit" loading={loading} loadingLabel="Saving…" className="flex-1">
            {submitLabel}
          </PrimaryButton>
        </div>
      </form>
    </AuthLayout>
  );
}

/* ---------- Step 1: Your details ---------- */

const pwRules = [
  { label: "8 or more characters", test: (v) => v.length >= 8 },
  { label: "A letter and a number", test: (v) => /[a-z]/i.test(v) && /\d/.test(v) },
];

export function BasicStep() {
  const { data, set } = useSignup();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async () => {
    const e = {};
    if (!data.name.trim()) e.name = "Enter your full name";
    if (!data.email.trim()) e.email = "Enter your work email";
    else if (!isEmail(data.email)) e.email = "Enter an email like name@clinic.in";
    const digits = data.phone.replace(/\D/g, "");
    if (!data.country) e.country = "Choose your country";
    if (!digits) e.phone = "Enter your mobile number";
    else if (data.dialCode === "+91" && digits.length !== 10) e.phone = "Mobile number must be 10 digits";
    else if (data.dialCode !== "+91" && (digits.length < 6 || digits.length > 14)) e.phone = "Enter the number without the country code, 6 to 14 digits";
    if (!pwRules.every((r) => r.test(data.password))) e.password = "Password must have 8+ characters, a letter and a number";
    if (!data.agree) e.agree = "Accept the terms to continue";
    setErrors(e);
    setFormError("");
    if (Object.keys(e).length) return;

    // An unverified sign-up for this email doesn't count as "exists" on the server,
    // so check-email answers correctly on a second try too.
    setLoading(true);
    try {
      await authAPI.checkEmail(data.email.trim());
      setLoading(false);
      navigate(stepPath("organisation"));
    } catch (err) {
      setLoading(false);
      if (err.response?.data?.exists) {
        setErrors({ email: "This email is already registered. Please login or use a different email." });
        return;
      }
      setFormError(err.response?.data?.message || "Error checking email");
    }
  };

  return (
    <SignupShell
      step="basic"
      title="Create your account"
      sub="You'll be the admin for your clinic on Olli"
      onSubmit={onSubmit}
      loading={loading}
      error={formError}
      aside={<Showcase step="basic" data={data} />}
    >
      <Field id="name" label="Full name" error={errors.name}>
        <TextInput id="name" autoComplete="name" value={data.name} onChange={(e) => set({ name: e.target.value })} invalid={!!errors.name} />
      </Field>
      <Field id="email" label="Work email" error={errors.email}>
        <TextInput id="email" type="email" autoComplete="email" value={data.email} onChange={(e) => set({ email: e.target.value })} invalid={!!errors.email} />
      </Field>
      <Field id="country" label="Country" hint="Where your clinic is" error={errors.country}>
        <SearchSelect id="country" value={data.country} options={COUNTRY_OPTIONS} onChange={(country) => set({ country, ...(dialCodeOf(country) ? { dialCode: dialCodeOf(country) } : {}), ...(country !== data.country ? { state: "", pin: "" } : {}) })} searchable searchPlaceholder="Search countries" invalid={!!errors.country} className="w-full" />
      </Field>
      <Field id="phone" label="Mobile number" error={errors.phone}>
        <div className="flex gap-2">
          <div className="w-32 shrink-0"><SearchSelect id="dialCode" value={data.dialCode} options={DIAL_OPTIONS} onChange={(dialCode) => set({ dialCode })} searchable searchPlaceholder="Code or country" ariaLabel="Country code" className="w-full" /></div>
          <div className="min-w-0 flex-1">
            <TextInput
              id="phone"
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              value={data.phone}
              onChange={(e) => set({ phone: e.target.value.replace(/[^\d ]/g, "").slice(0, 16) })}
              invalid={!!errors.phone}
            />
          </div>
        </div>
      </Field>
      <Field id="password" label="Password" error={errors.password}>
        <PasswordInput
          id="password"
          autoComplete="new-password"
          value={data.password}
          onChange={(e) => set({ password: e.target.value })}
          invalid={!!errors.password}
        />
      </Field>
      {!errors.password && (
        <ul className="-mt-2 flex flex-wrap gap-x-5 gap-y-1">
          {pwRules.map((r) => {
            const ok = r.test(data.password);
            return (
              <li key={r.label} className={ok ? "flex items-center gap-1.5 text-caption font-medium text-success" : "flex items-center gap-1.5 text-caption text-muted"}>
                <Check size={14} strokeWidth={2} className={ok ? "" : "opacity-40"} />
                {r.label}
              </li>
            );
          })}
        </ul>
      )}
      <div className="flex flex-col gap-1.5 pt-1">
        <Checkbox id="agree" checked={data.agree} onChange={(v) => set({ agree: v })} invalid={!!errors.agree}>
          I agree to the{" "}
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="font-medium underline underline-offset-2">Terms of service</a> and{" "}
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="font-medium underline underline-offset-2">Privacy policy</a>
        </Checkbox>
        {errors.agree && <p className="pl-8 text-caption font-medium text-danger">{errors.agree}</p>}
      </div>
    </SignupShell>
  );
}

/* ---------- Step 2: Organisation ---------- */

export function OrgStep() {
  const { data, set } = useSignup();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});

  const india = isIndia(data);
  const onSubmit = () => {
    const e = {};
    if (!data.orgName.trim()) e.orgName = "Enter your clinic or hospital name";
    if (!data.orgType) e.orgType = "Choose the type that fits best";
    if (!data.address.trim()) e.address = "Enter the street address";
    if (!data.city.trim()) e.city = "Enter the city";
    if (india) {
      if (!data.state) e.state = "Choose the state";
      if (!/^\d{6}$/.test(data.pin)) e.pin = data.pin ? "PIN code must be 6 digits" : "Enter the PIN code";
    } else if (data.pin && !/^[A-Za-z0-9][A-Za-z0-9 -]{1,9}$/.test(data.pin.trim())) e.pin = "Postal code is 2 to 10 letters or digits";
    setErrors(e);
    if (Object.keys(e).length === 0) navigate(stepPath("tax"));
  };

  return (
    <SignupShell
      step="organisation"
      title="About your organisation"
      sub="Olli uses this to introduce your clinic to patients"
      back={stepPath("basic")}
      onSubmit={onSubmit}
      aside={<Showcase step="organisation" data={data} />}
    >
      <Field id="orgName" label="Clinic or hospital name" error={errors.orgName}>
        <TextInput id="orgName" autoComplete="organization" value={data.orgName} onChange={(e) => set({ orgName: e.target.value })} invalid={!!errors.orgName} />
      </Field>
      <Field id="orgType" label="Type" error={errors.orgType}>
        <SelectInput id="orgType" placeholder="Choose a type" options={orgTypes} value={data.orgType} onChange={(e) => set({ orgType: e.target.value })} invalid={!!errors.orgType} />
      </Field>
      <Field id="address" label="Street address" error={errors.address}>
        <TextInput id="address" autoComplete="street-address" value={data.address} onChange={(e) => set({ address: e.target.value })} invalid={!!errors.address} />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field id="city" label="City" error={errors.city}>
          <TextInput id="city" autoComplete="address-level2" value={data.city} onChange={(e) => set({ city: e.target.value })} invalid={!!errors.city} />
        </Field>
        <Field id="pin" label={india ? "PIN code" : "Postal code"} optional={!india} error={errors.pin}>
          <TextInput
            id="pin"
            inputMode={india ? "numeric" : "text"}
            autoComplete="postal-code"
            value={data.pin}
            onChange={(e) => set({ pin: india ? e.target.value.replace(/\D/g, "").slice(0, 6) : e.target.value.toUpperCase().replace(/[^A-Z0-9 -]/g, "").slice(0, 10) })}
            invalid={!!errors.pin}
          />
        </Field>
      </div>
      {india ? (
        <Field id="state" label="State" error={errors.state}>
          <SelectInput id="state" placeholder="Choose a state" options={indianStates} value={data.state} onChange={(e) => set({ state: e.target.value })} invalid={!!errors.state} />
        </Field>
      ) : (
        <Field id="state" label="State, province or region" optional hint={data.country}>
          <TextInput id="state" autoComplete="address-level1" value={data.state} onChange={(e) => set({ state: e.target.value })} />
        </Field>
      )}
      <Field id="website" label="Website" optional>
        <PrefixInput id="website" prefix="https://" type="url" value={data.website} onChange={(e) => set({ website: e.target.value.replace(/^https?:\/\//, "") })} />
      </Field>
    </SignupShell>
  );
}

/* ---------- Step 3: Tax details ---------- */

const GSTIN_RE = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const PAN_RE = /^[A-Z]{5}\d{4}[A-Z]$/;

export function TaxStep() {
  const { data, set } = useSignup();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);

  const setGstin = (raw) => {
    const gstin = raw.toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 15);
    const patch = { gstin };
    // PAN is characters 3 to 12 of a GSTIN: fill it in when it's still empty or was filled from the GSTIN.
    if (gstin.length >= 12 && (!data.pan || data.pan === data.gstin.slice(2, 12))) patch.pan = gstin.slice(2, 12);
    set(patch);
  };

  const india = isIndia(data);
  const onSubmit = async () => {
    const e = {};
    if (!india) {
      if (data.taxId && !/^[A-Za-z0-9][A-Za-z0-9 ./-]{2,29}$/.test(data.taxId.trim())) e.taxId = "Tax ID is 3 to 30 letters, digits, dots or dashes";
    } else if (data.hasGst) {
      if (!data.gstin) e.gstin = "Enter your GSTIN";
      else if (!GSTIN_RE.test(data.gstin)) e.gstin = "GSTIN must be 15 characters, like 27AAHCS1234K1Z5";
      else if (data.state && gstStateCodes[data.state] && data.gstin.slice(0, 2) !== gstStateCodes[data.state])
        e.gstin = `This GSTIN isn't registered in ${data.state}. Check the number or your organisation's state`;
    }
    if (!data.legalName.trim()) e.legalName = "Enter the legal name on your registration";
    if (india && !data.pan) e.pan = "Enter your PAN";
    else if (india && !PAN_RE.test(data.pan)) e.pan = "PAN must be 10 characters, like AAHCS1234K";
    setErrors(e);
    setFormError("");
    if (Object.keys(e).length) return;

    const email = data.email.trim();
    // A code is already out for exactly these details; Verify can resend it.
    if (data.otpSentTo === email && data.otpKey === otpKeyOf(data)) {
      navigate(stepPath("verify"));
      return;
    }
    setLoading(true);
    try {
      const response = await authAPI.sendOTP({
        name: data.name,
        email,
        password: data.password,
        phone: data.phone.replace(/\D/g, ""),
        countryCode: data.dialCode || "+91",
        channel: "whatsapp",
      });
      setLoading(false);
      if (response.data.success) {
        set({
          otpSentTo: email,
          otpKey: otpKeyOf(data),
          otpChannel: "whatsapp",
          otpVia: response.data.channel === "whatsapp" ? "whatsapp" : "email",
          otpWhatsappError: response.data.whatsapp_error || "",
        });
        navigate(stepPath("verify"));
      }
    } catch (err) {
      setLoading(false);
      setFormError(err.response?.data?.message || "Failed to send OTP. Please try again.");
    }
  };

  return (
    <SignupShell
      step="tax"
      title="Tax details"
      sub={india ? "These appear on the invoices Olli sends you" : "These appear on the invoices Olli sends you. Clinics outside India start with a 7-day trial of our top plan"}
      back={stepPath("organisation")}
      onSubmit={onSubmit}
      loading={loading}
      error={formError}
      aside={<Showcase step="tax" data={data} />}
    >
      {!india && (
        <Field id="taxId" label="Tax ID or VAT number" optional hint={`Your business tax number in ${data.country}`} error={errors.taxId}>
          <TextInput id="taxId" value={data.taxId} onChange={(e) => set({ taxId: e.target.value.slice(0, 30) })} invalid={!!errors.taxId} className="uppercase" autoComplete="off" />
        </Field>
      )}
      {india && <div className="flex flex-col gap-1.5">
        <span id="gst-label" className="text-small font-medium">Registered for GST?</span>
        <div role="radiogroup" aria-labelledby="gst-label" className="grid grid-cols-2 gap-1 rounded-full bg-mist p-1">
          {[
            { v: true, label: "Yes" },
            { v: false, label: "No" },
          ].map((o) => (
            <button
              key={o.label}
              type="button"
              role="radio"
              aria-checked={data.hasGst === o.v}
              onClick={() => {
                set({ hasGst: o.v });
                setErrors((er) => ({ ...er, gstin: undefined }));
              }}
              className={data.hasGst === o.v ? "h-10 rounded-full bg-paper text-small font-semibold shadow-soft" : "h-10 rounded-full text-small font-medium text-muted hover:text-ink"}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>}

      {india && data.hasGst && (
        <Field id="gstin" label="GSTIN" hint="15 characters. We'll fill in your PAN from it" error={errors.gstin}>
          <TextInput id="gstin" value={data.gstin} onChange={(e) => setGstin(e.target.value)} invalid={!!errors.gstin} className="uppercase tabular-nums" autoComplete="off" />
        </Field>
      )}
      <Field id="legalName" label="Legal business name" hint={india ? "As on your GST or PAN registration" : "As on your business registration"} error={errors.legalName}>
        <TextInput id="legalName" value={data.legalName} onChange={(e) => set({ legalName: e.target.value })} invalid={!!errors.legalName} />
      </Field>
      {india && <Field id="pan" label="PAN" error={errors.pan}>
        <TextInput
          id="pan"
          value={data.pan}
          onChange={(e) => set({ pan: e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 10) })}
          invalid={!!errors.pan}
          className="uppercase tabular-nums"
          autoComplete="off"
        />
      </Field>}
    </SignupShell>
  );
}

/* ---------- Router ---------- */

/** Later steps need the earlier ones (state is in memory, so a refresh starts over). */
function Needs({ ok, children }) {
  return ok ? children : <Navigate to={stepPath("basic")} replace />;
}

function SignupSteps() {
  const { data } = useSignup();
  const hasDetails = !!(data.email && data.password);
  return (
    <Routes>
      <Route index element={<Navigate to={stepPath("basic")} replace />} />
      <Route path="basic" element={<BasicStep />} />
      <Route path="organisation" element={<Needs ok={hasDetails}><OrgStep /></Needs>} />
      <Route path="tax" element={<Needs ok={hasDetails && !!data.orgName}><TaxStep /></Needs>} />
      <Route path="verify" element={<Needs ok={!!data.otpSentTo}><Verify /></Needs>} />
      <Route path="setup" element={<Needs ok={data.verified}><Setup /></Needs>} />
      <Route path="*" element={<Navigate to={stepPath("basic")} replace />} />
    </Routes>
  );
}

export default function Signup() {
  // Read once on arrival: a token set by Verify later on must not bounce the Account setup step.
  const [signedIn] = useState(() => !!localStorage.getItem("token"));
  if (signedIn) return <Navigate to={AUTH_PATHS.dashboard} replace />;
  return (
    <SignupProvider>
      <SignupSteps />
    </SignupProvider>
  );
}
